import React, { useEffect, useMemo, useState } from 'react';
import { reportsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Activity, ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';

// F5: who uses the app. Last seen = any real use (opening the app, loading a
// screen, an action); the notification bell's background check is ignored.
// Activity is counted in ~10-minute stretches, so time figures are approximate.

const toDate = (d) => {
  if (!d) return null;
  const s = String(d);
  return new Date(s.endsWith('Z') || /[+-]\d\d:\d\d$/.test(s) ? s : `${s}Z`);
};

const istDayKey = (date) =>
  new Date(date.getTime() + 330 * 60000).toISOString().slice(0, 10);

const timeOnly = (d) =>
  d ? d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '—';

const relative = (d) => {
  if (!d) return 'Never';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const today = istDayKey(new Date());
  const yesterday = istDayKey(new Date(Date.now() - 86400000));
  const day = istDayKey(d);
  if (day === today) return `Today ${timeOnly(d)}`;
  if (day === yesterday) return `Yesterday ${timeOnly(d)}`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ` ${timeOnly(d)}`;
};

const approxTime = (windows, perWindow) => {
  const mins = (windows || 0) * perWindow;
  if (!mins) return '—';
  if (mins < 60) return `~${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `~${h} h ${m} min` : `~${h} h`;
};

const ROLE_LABEL = {
  owner: 'Owner', admin: 'Admin', coordinator: 'Coordinator', supervisor: 'Supervisor',
  machine_operator: 'Pressing operator', cutting_operator: 'Cutting operator',
  boring_operator: 'Boring operator', accountant: 'Accountant', client: 'Client',
};

const REASON = { wrong_password: 'Wrong password', unknown_mobile: 'Number not registered' };

const UsageReport = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [openUser, setOpenUser] = useState(null);
  const [userDays, setUserDays] = useState({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await reportsAPI.getUsage(days));
      setUserDays({});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load usage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const toggleUser = async (id) => {
    if (openUser === id) { setOpenUser(null); return; }
    setOpenUser(id);
    if (!userDays[id]) {
      try {
        const r = await reportsAPI.getUsage(days, id);
        setUserDays((prev) => ({ ...prev, [id]: r.user_days || [] }));
      } catch {
        setUserDays((prev) => ({ ...prev, [id]: [] }));
      }
    }
  };

  const perWindow = data?.minutes_per_window || 10;
  const today = istDayKey(new Date());

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.users || []).filter((u) =>
      !q || (u.name || '').toLowerCase().includes(q) || (ROLE_LABEL[u.role] || u.role || '').toLowerCase().includes(q));
  }, [data, search]);

  const counts = useMemo(() => {
    const users = data?.users || [];
    return {
      activeToday: users.filter((u) => u.last_seen && istDayKey(toDate(u.last_seen)) === today).length,
      loggedInToday: new Set((data?.logins_today || []).map((l) => l.name)).size,
      failed: (data?.failed_24h || []).length,
      quiet: users.filter((u) => u.idle_days !== null && u.idle_days >= 30).length,
      neverSeen: users.filter((u) => !u.last_seen).length,
    };
  }, [data, today]);

  return (
    <div className="space-y-4" data-testid="usage-report">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5" /> Team usage
            </CardTitle>
            <div className="flex items-center gap-2">
              {[7, 30].map((d) => (
                <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)}>
                  Last {d} days
                </Button>
              ))}
              <Button size="sm" variant="ghost" onClick={load} disabled={loading} title="Refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Last seen counts real use: opening the app, loading a screen or taking an action. Time is counted in
            ~{perWindow}-minute stretches, so it's approximate. Recording started when this feature went live; earlier
            days are blank.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg bg-green-50 p-3"><p className="text-xs text-green-700">Used the app today</p><p className="text-2xl font-bold text-green-800">{counts.activeToday}</p></div>
            <div className="rounded-lg bg-blue-50 p-3"><p className="text-xs text-blue-700">Logged in today</p><p className="text-2xl font-bold text-blue-800">{counts.loggedInToday}</p></div>
            <div className={`rounded-lg p-3 ${counts.failed ? 'bg-red-50' : 'bg-gray-50'}`}><p className={`text-xs ${counts.failed ? 'text-red-700' : 'text-gray-600'}`}>Failed logins (24 h)</p><p className={`text-2xl font-bold ${counts.failed ? 'text-red-800' : 'text-gray-700'}`}>{counts.failed}</p></div>
            <div className="rounded-lg bg-amber-50 p-3"><p className="text-xs text-amber-700">Quiet 30+ days</p><p className="text-2xl font-bold text-amber-800">{counts.quiet}</p></div>
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input id="usage-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or role" className="pl-9" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border p-2 text-left">Name</th>
                  <th className="border p-2 text-left">Last seen</th>
                  <th className="border p-2 text-left">Last login</th>
                  <th className="border p-2 text-center">Days active</th>
                  <th className="border p-2 text-center">Time (approx.)</th>
                  <th className="border p-2 text-center">Logins</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const seen = toDate(u.last_seen);
                  const open = openUser === u.id;
                  return (
                    <React.Fragment key={u.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleUser(u.id)} data-testid={`usage-row-${u.id}`}>
                        <td className="border p-2">
                          <div className="flex items-center gap-1.5">
                            {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <span className="font-medium">{u.name}</span>
                            <Badge variant="outline" className="text-[10px]">{ROLE_LABEL[u.role] || u.role}</Badge>
                          </div>
                        </td>
                        <td className={`border p-2 whitespace-nowrap ${!seen ? 'text-gray-400' : u.idle_days >= 30 ? 'text-amber-700' : ''}`}>{relative(seen)}</td>
                        <td className="border p-2 whitespace-nowrap text-gray-600">{u.last_login ? relative(toDate(u.last_login)) : '—'}</td>
                        <td className="border p-2 text-center tabular-nums">{u.days_active || '—'}</td>
                        <td className="border p-2 text-center whitespace-nowrap">{approxTime(u.active_windows, perWindow)}</td>
                        <td className="border p-2 text-center tabular-nums">
                          {u.logins || '—'}
                          {u.failed_logins > 0 && <span className="ml-1 text-xs text-red-600">({u.failed_logins} failed)</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} className="border p-3 bg-gray-50">
                            {!userDays[u.id] ? (
                              <p className="text-xs text-gray-500">Loading…</p>
                            ) : userDays[u.id].length === 0 ? (
                              <p className="text-xs text-gray-500">No activity recorded for {u.name} in the last {days} days.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="text-left py-1">Day</th>
                                    <th className="text-left py-1">First seen</th>
                                    <th className="text-left py-1">Last seen</th>
                                    <th className="text-left py-1">Time (approx.)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {userDays[u.id].map((d) => (
                                    <tr key={d.day}>
                                      <td className="py-1">{new Date(`${d.day}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}</td>
                                      <td className="py-1">{timeOnly(toDate(d.first_seen))}</td>
                                      <td className="py-1">{timeOnly(toDate(d.last_seen))}</td>
                                      <td className="py-1">{approxTime(d.windows, perWindow)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {data && rows.length === 0 && (
                  <tr><td colSpan={6} className="border p-4 text-center text-gray-500">No one matches "{search}".</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {data && counts.neverSeen > 0 && (
            <p className="text-xs text-gray-500">{counts.neverSeen} account{counts.neverSeen > 1 ? 's have' : ' has'} not used the app since recording started.</p>
          )}
        </CardContent>
      </Card>

      {data?.failed_24h?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base text-red-700">Failed logins in the last 24 hours</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead className="bg-red-50"><tr><th className="border p-2 text-left">When</th><th className="border p-2 text-left">Who / number</th><th className="border p-2 text-left">Reason</th><th className="border p-2 text-left">IP</th></tr></thead>
                <tbody>
                  {data.failed_24h.map((f, i) => (
                    <tr key={i}>
                      <td className="border p-2 whitespace-nowrap">{relative(toDate(f.at))}</td>
                      <td className="border p-2">{f.name || '—'} <span className="text-gray-500">{f.mobile}</span></td>
                      <td className="border p-2">{REASON[f.reason] || f.reason}</td>
                      <td className="border p-2 text-gray-500">{f.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UsageReport;
